import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { apiFetch } from '../../auth/apiFetch';
import './UserManagementPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const VALID_ROLES = [
  'ADMIN',
  'PROJECT_LEAD',
  'FIELD_REP',
  'DISPOSAL_LEAD',
  'SUPPLIER_USER',
  'SUPPLIER_MANAGER',
];

const ROLE_LABELS = {
  ADMIN:            'Admin',
  PROJECT_LEAD:     'Project Lead',
  FIELD_REP:        'Field Rep',
  DISPOSAL_LEAD:    'Disposal Lead',
  SUPPLIER_USER:    'Supplier User',
  SUPPLIER_MANAGER: 'Supplier Manager',
};

// Canonical GUID, 8-4-4-4-12 hex, case-insensitive — the shape the Entra user
// blade shows. Shape only; we cannot verify the value exists in the directory.
const ENTRA_OID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ENTRA_OID_HELP =
  'Copy verbatim from the Entra user blade. Never type or construct it.';

/**
 * Shape-check an Object ID field. Returns an error string, or null when the
 * value is acceptable.
 *
 * ⚠ EMPTY IS VALID and must stay that way. A blank field means "leave the
 * stored value alone": handleSave deletes the key from the PATCH body, and the
 * API treats an omitted key as no-change while an explicit "" or null CLEARS
 * the stored Object ID (function_app.py:1432). Returning an error for blank
 * would make every unrelated edit — a display-name change, a site swap —
 * unsavable on any row whose Object ID we cannot read back.
 */
function entraOidError(value) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null; // blank = no change; always valid
  if (!ENTRA_OID_PATTERN.test(trimmed)) {
    return 'Object ID must be a GUID in 8-4-4-4-12 form, e.g. 0f8fad5b-d9cb-469f-a165-70867728950e. Paste it from the Entra user blade without editing it.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// SitesPicker
// ---------------------------------------------------------------------------
function SitesPicker({ assignedSites, allSites, onChange }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openUp, setOpenUp] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggleDropdown() {
    if (!dropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceAbove > spaceBelow);
    }
    setDropdownOpen(p => !p);
  }

  const isAllSites = assignedSites.includes('*');

  function removeSite(siteId) {
    onChange(assignedSites.filter(s => s !== siteId));
  }

  function addSite(siteId) {
    if (!assignedSites.includes(siteId)) {
      onChange([...assignedSites, siteId]);
    }
    setDropdownOpen(false);
    setSearch('');
  }

  function toggleAllSites() {
    onChange(isAllSites ? [] : ['*']);
  }

  const availableSites = allSites.filter(s =>
    !assignedSites.includes(s.site_id) &&
    (
      (s.site_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.site_id   || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div className="sp-wrapper">
      <label className="sp-all-toggle">
        <input
          type="checkbox"
          checked={isAllSites}
          onChange={toggleAllSites}
          className="sp-checkbox"
        />
        <span>All Sites</span>
      </label>

      {!isAllSites && (
        <div className="sp-chips">
          {assignedSites.map(siteId => {
            const site = allSites.find(s => s.site_id === siteId);
            const label = site ? (site.site_name || siteId) : siteId;
            return (
              <span key={siteId} className="sp-chip">
                {label}
                <button className="sp-chip-remove" onClick={() => removeSite(siteId)}>✕</button>
              </span>
            );
          })}
          <div className="sp-add-wrapper" ref={dropdownRef}>
            <button className="sp-add-btn" ref={triggerRef} onClick={toggleDropdown}>
              + Add
            </button>
            {dropdownOpen && (
              <div className={`sp-dropdown ${openUp ? 'sp-dropdown-up' : 'sp-dropdown-down'}`}>
                <input
                  type="text"
                  className="sp-dropdown-search"
                  placeholder="Search sites..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
                <div className="sp-dropdown-list">
                  {availableSites.length === 0 ? (
                    <div className="sp-dropdown-empty">No sites available</div>
                  ) : (
                    availableSites.map(site => (
                      <button
                        key={site.site_id}
                        className="sp-dropdown-item"
                        onClick={() => addSite(site.site_id)}
                      >
                        <span className="sp-dropdown-name">{site.site_name || site.site_id}</span>
                        <span className="sp-dropdown-id">{site.site_id}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddUserRow
// ---------------------------------------------------------------------------
function AddUserRow({ allSites, callerEmail, onSaved, onCancel }) {
  const [form, setForm] = useState({
    email: '', display_name: '', role: 'SUPPLIER_USER', sites: [], is_active: true,
    entra_oid: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  // Live shape check, recomputed each render — drives both the inline error and
  // the disabled Save. Blank stays valid (see entraOidError).
  const oidError = entraOidError(form.entra_oid);

  async function handleSave() {
    setError(null);
    if (!form.email.trim()) { setError('Email is required.'); return; }
    // Blocked before the request: a malformed Object ID is never sent, never
    // silently dropped, and never trimmed into shape.
    if (oidError) { setError(oidError); return; }
    try {
      setSaving(true);
      const res = await apiFetch(
        `${API_BASE_URL}/manage_user?caller_email=${encodeURIComponent(callerEmail)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            email: form.email.trim(),
            display_name: form.display_name.trim(),
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `API error: ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <tr className="um-row-adding">
        <td>
          <input
            type="email"
            value={form.email}
            onChange={e => handleChange('email', e.target.value)}
            placeholder="user@company.com"
            className="um-inline-input"
            autoFocus
          />
          <div className="um-helper-text">Enter the user's real email address (e.g. jsmith@supplier.com) — the same address they sign in with. Supplier accounts are no exception.</div>
          {/* Label above, helper between label and input: the operator reads
              the instruction before the field, and it stays visible after the
              placeholder is gone. */}
          <label className="um-field-label" htmlFor="um-oid-new">Entra Object ID</label>
          <div className="um-helper-text">{ENTRA_OID_HELP}</div>
          <input
            id="um-oid-new"
            type="text"
            value={form.entra_oid}
            onChange={e => handleChange('entra_oid', e.target.value)}
            placeholder="Entra Object ID"
            className={`um-inline-input${oidError ? ' um-input-invalid' : ''}`}
            aria-invalid={oidError ? 'true' : undefined}
            aria-describedby={oidError ? 'um-oid-new-error' : undefined}
          />
          {oidError && (
            <div className="um-field-error" id="um-oid-new-error" data-testid="um-oid-error-new">
              {oidError}
            </div>
          )}
        </td>
        <td>
          <input
            type="text"
            value={form.display_name}
            onChange={e => handleChange('display_name', e.target.value)}
            placeholder="Display name"
            className="um-inline-input"
          />
        </td>
        <td>
          <select
            value={form.role}
            onChange={e => handleChange('role', e.target.value)}
            className="um-inline-select"
          >
            {VALID_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </td>
        <td colSpan={2}>
          <SitesPicker
            assignedSites={form.sites}
            allSites={allSites}
            onChange={sites => handleChange('sites', sites)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={e => handleChange('is_active', e.target.checked)}
            className="um-checkbox"
          />
        </td>
        <td>
          <div className="um-inline-actions">
            <button
              className="um-save-inline"
              onClick={handleSave}
              disabled={saving || !!oidError}
              title={oidError ? 'Fix the Entra Object ID first' : undefined}
            >
              {saving ? '...' : 'Save'}
            </button>
            <button className="um-cancel-inline" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
      {error && (
        <tr className="um-row-error">
          <td colSpan={7}><div className="um-inline-error">{error}</div></td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// UserRow
// ---------------------------------------------------------------------------
function UserRow({ user, allSites, callerEmail, onSaved, submittedOid, onOidSubmitted }) {
  const { userProfile } = useUser();
  const isAdmin = userProfile?.role === 'ADMIN';

  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  // submittedOid is a PROP, not row state. It used to be useState here and never
  // rendered: onSaved -> loadUsers sets loading=true, and the parent's loading
  // branch returns a placeholder INSTEAD of the <table>, so every UserRow
  // unmounts mid-refetch and remounts with fresh state. A stable key cannot
  // preserve state across a subtree that stops rendering. The parent owns the
  // value now, keyed by the same identifier the rows are keyed on.
  //
  // It is still not a read-back: the API denylists entra_oid from get_users
  // (function_app.py:1375-1378) and from every handler that emits a Users
  // document (:1644-1649), so there is nothing to read back.

  // Per-row DOM id: this component renders once per user, so a hardcoded id
  // would collide across rows and break label association.
  const oidInputId = `um-oid-${user.user_id || user.id || user.email}`;

  // Email edit state (separate from row edit)
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState(null);

  function startEmailEdit() {
    setNewEmail(user.email);
    setEmailError(null);
    setEditingEmail(true);
    setEmailConfirm(false);
  }

  function cancelEmailEdit() {
    setEditingEmail(false);
    setEmailConfirm(false);
    setEmailError(null);
  }

  async function confirmEmailSave() {
    setEmailSaving(true);
    setEmailError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/manage_user?caller_email=${encodeURIComponent(callerEmail)}&email=${encodeURIComponent(user.email)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newEmail.trim() }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409) {
          setEmailError('A user with this email already exists.');
        } else if (res.status === 400) {
          setEmailError(err.error || 'Invalid email.');
        } else {
          setEmailError(err.error || `Save failed (${res.status})`);
        }
        setEmailConfirm(false);
        return;
      }
      setEditingEmail(false);
      setEmailConfirm(false);
      onSaved();
    } catch (err) {
      setEmailError(err.message);
      setEmailConfirm(false);
    } finally {
      setEmailSaving(false);
    }
  }

  function startEdit() {
    setForm({
      display_name: user.display_name || '',
      role:         user.role,
      sites:        [...(user.sites || [])],
      is_active:    user.is_active,
      // Always blank: the API denylists entra_oid from every read, so there is no
      // stored value to prefill. Left blank on save it is omitted from the body,
      // which the API treats as "leave alone" (an explicit "" would CLEAR it).
      entra_oid:    '',
    });
    setError(null);
    onOidSubmitted(user.email, null); // stale echo must not survive into a new edit
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  // Live shape check, recomputed each render — drives the inline error and the
  // disabled Save. Blank stays valid (see entraOidError).
  const oidError = entraOidError(form.entra_oid);

  async function handleSave() {
    setError(null);
    // Blocked before the request: a malformed Object ID is never sent, never
    // silently dropped, and never trimmed into shape.
    if (oidError) { setError(oidError); return; }
    try {
      setSaving(true);
      // Omit entra_oid entirely unless the operator typed one. Sending "" would
      // be read as an explicit CLEAR and would wipe a stored Object ID on an
      // unrelated edit (e.g. a display-name change).
      const payload = { ...form };
      let sentOid = null;
      if (payload.entra_oid && payload.entra_oid.trim()) {
        payload.entra_oid = payload.entra_oid.trim();
        sentOid = payload.entra_oid;
      } else {
        delete payload.entra_oid;
      }
      const res = await apiFetch(
        `${API_BASE_URL}/manage_user?caller_email=${encodeURIComponent(callerEmail)}&email=${encodeURIComponent(user.email)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `API error: ${res.status}`);
      }
      // Only after a 2xx, and only when an Object ID was actually in the body.
      // Reported UP so it survives the refetch onSaved is about to trigger.
      // Must run BEFORE onSaved(): loadUsers unmounts this row.
      onOidSubmitted(user.email, sentOid);
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <>
        <tr className="um-row-editing">
          <td className="um-email">
            {user.email}
            {/* Label above, helper between label and input — same order as the
                add row, so an operator who learns one knows the other. */}
            <label className="um-field-label" htmlFor={oidInputId}>Entra Object ID</label>
            <div className="um-helper-text">{ENTRA_OID_HELP} Leave blank to keep the current value.</div>
            <input
              id={oidInputId}
              type="text"
              value={form.entra_oid}
              onChange={e => handleChange('entra_oid', e.target.value)}
              placeholder="Entra Object ID"
              className={`um-inline-input${oidError ? ' um-input-invalid' : ''}`}
              aria-invalid={oidError ? 'true' : undefined}
              aria-describedby={oidError ? `${oidInputId}-error` : undefined}
            />
            {oidError && (
              <div className="um-field-error" id={`${oidInputId}-error`} data-testid="um-oid-error">
                {oidError}
              </div>
            )}
          </td>
          <td>
            <input
              type="text"
              value={form.display_name}
              onChange={e => handleChange('display_name', e.target.value)}
              className="um-inline-input"
              autoFocus
            />
          </td>
          <td>
            <select
              value={form.role}
              onChange={e => handleChange('role', e.target.value)}
              className="um-inline-select"
            >
              {VALID_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </td>
          <td colSpan={2}>
            <SitesPicker
              assignedSites={form.sites}
              allSites={allSites}
              onChange={sites => handleChange('sites', sites)}
            />
          </td>
          <td>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => handleChange('is_active', e.target.checked)}
              className="um-checkbox"
            />
          </td>
          <td>
            <div className="um-inline-actions">
              <button
                className="um-save-inline"
                onClick={handleSave}
                disabled={saving || !!oidError}
                title={oidError ? 'Fix the Entra Object ID first' : undefined}
              >
                {saving ? '...' : 'Save'}
              </button>
              <button className="um-cancel-inline" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
            </div>
          </td>
        </tr>
        {error && (
          <tr className="um-row-error">
            <td colSpan={7}><div className="um-inline-error">{error}</div></td>
          </tr>
        )}
      </>
    );
  }

  return (
    <>
    <tr
      className={`um-row ${!user.is_active ? 'um-row-inactive' : ''}`}
      onClick={startEdit}
      title="Click to edit"
    >
      <td className="um-email">
        {editingEmail ? (
          <div className="um-email-edit" onClick={e => e.stopPropagation()}>
            <input
              type="email"
              className="um-inline-input"
              value={newEmail}
              onChange={e => { setNewEmail(e.target.value); setEmailError(null); }}
              autoFocus
            />
            <button className="um-save-inline" disabled={emailSaving}
              onClick={() => setEmailConfirm(true)}>Save</button>
            <button className="um-cancel-inline" disabled={emailSaving}
              onClick={cancelEmailEdit}>Cancel</button>
            {emailError && <div className="um-inline-error um-email-error">{emailError}</div>}
          </div>
        ) : (
          <span className="um-email-display">
            {user.email}
            {isAdmin && (
              <button className="um-email-edit-btn" onClick={e => { e.stopPropagation(); startEmailEdit(); }}
                title="Edit email">&#9998;</button>
            )}
          </span>
        )}
        {submittedOid && (
          <div className="um-oid-echo" data-testid="um-oid-echo">
            <span className="um-oid-echo-label">Object ID submitted:</span>
            <code className="um-oid-echo-value">{submittedOid}</code>
            <span className="um-oid-echo-note">
              This is the value this browser sent and the server accepted. It is
              not read back from the database — ToolSight never returns a stored
              Object ID. Compare it against the Entra user blade.
            </span>
          </div>
        )}
      </td>
      <td>{user.display_name || '—'}</td>
      <td>
        <span className={`um-role-badge um-role-${user.role?.toLowerCase()}`}>
          {ROLE_LABELS[user.role] || user.role}
        </span>
      </td>
      <td colSpan={2} className="um-sites">
        {(user.sites || []).includes('*') ? (
          <span className="um-sites-all">All Sites</span>
        ) : (user.sites || []).length === 0 ? '—' : (
          <div className="um-site-chips-readonly">
            {(user.sites || []).map(siteId => (
              <span key={siteId} className="um-chip-readonly">{siteId}</span>
            ))}
          </div>
        )}
      </td>
      <td>
        <span className={`um-status-badge ${user.is_active ? 'um-active' : 'um-inactive'}`}>
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="um-col-actions">
        <span className="um-edit-hint">Edit</span>
      </td>
    </tr>
    {emailConfirm && (
      <tr className="um-row-confirm">
        <td colSpan={7}>
          <div className="um-confirm-dialog" onClick={e => e.stopPropagation()}>
            <strong className="um-confirm-title">Change User Email?</strong>
            <p className="um-confirm-body">
              This will update the login email for <strong>{user.email}</strong>. The user must log in
              with the new email address immediately after this change. If the email does not match
              their Microsoft Entra account, they will lose access.
            </p>
            <p className="um-helper-text">Enter the user's real email address (e.g. jsmith@supplier.com) — the same address they sign in with. Supplier accounts are no exception.</p>
            <div className="um-confirm-actions">
              <button className="um-cancel-inline" disabled={emailSaving}
                onClick={() => setEmailConfirm(false)}>Cancel</button>
              <button className="um-btn-danger" disabled={emailSaving}
                onClick={confirmEmailSave}>
                {emailSaving ? 'Saving…' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// UserManagementPage
// ---------------------------------------------------------------------------
export default function UserManagementPage() {
  const { callerEmail } = useUser();

  const [users,      setUsers]      = useState([]);
  const [allSites,   setAllSites]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [addingUser, setAddingUser] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterRole,   setFilterRole]   = useState('');
  const [filterActive, setFilterActive] = useState('true');

  // Submitted Object IDs, keyed by the SAME identifier the rows are keyed on
  // (user.email). Lives here, not in UserRow: onSaved -> loadUsers flips
  // loading=true, the loading branch below renders a placeholder instead of the
  // <table>, and every UserRow unmounts — row-local state cannot survive that.
  // A per-row map also guarantees one row's echo can never surface on another.
  const [submittedOids, setSubmittedOids] = useState({});
  const noteSubmittedOid = useCallback((email, oid) => {
    setSubmittedOids(prev => ({ ...prev, [email]: oid }));
  }, []);

  const loadUsers = useCallback(async () => {
    if (!callerEmail) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ caller_email: callerEmail });
      if (filterRole)   params.set('role',      filterRole);
      if (filterActive) params.set('is_active', filterActive);

      const res = await apiFetch(`${API_BASE_URL}/get_users?${params}`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();

      const search = filterSearch.toLowerCase();
      setUsers(search
        ? data.filter(u =>
            (u.email        || '').toLowerCase().includes(search) ||
            (u.display_name || '').toLowerCase().includes(search)
          )
        : data
      );
    } catch (err) {
      setError('Failed to load users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [callerEmail, filterRole, filterActive, filterSearch]);

  const loadSites = useCallback(async () => {
    if (!callerEmail) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/get_sites?caller_email=${encodeURIComponent(callerEmail)}`);
      if (!res.ok) return;
      setAllSites(await res.json());
    } catch (err) {
      console.warn('Could not load sites:', err.message);
    }
  }, [callerEmail]);

  useEffect(() => {
    if (callerEmail) { loadUsers(); loadSites(); }
  }, [callerEmail, loadUsers, loadSites]);

  return (
    <div className="um-page">
      <header className="um-header">
        <div className="um-header-left">
          <h1>USER MANAGEMENT</h1>
          <span className="um-badge">TMG INTERNAL</span>
        </div>
        <div className="um-header-right">
          <span className="um-count">{users.length} user{users.length !== 1 ? 's' : ''}</span>
          {!addingUser && (
            <button className="um-add-btn" onClick={() => setAddingUser(true)}>+ Add User</button>
          )}
        </div>
      </header>

      <div className="um-filters">
        <input
          type="text"
          placeholder="Search email or name..."
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          className="um-search"
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="um-select">
          <option value="">ALL ROLES</option>
          {VALID_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)} className="um-select">
          <option value="true">ACTIVE</option>
          <option value="false">INACTIVE</option>
          <option value="">ALL</option>
        </select>
        {(filterSearch || filterRole || filterActive !== 'true') && (
          <button className="um-clear" onClick={() => { setFilterSearch(''); setFilterRole(''); setFilterActive('true'); }}>
            Clear
          </button>
        )}
      </div>

      <div className="um-table-wrapper">
        {loading ? (
          <div className="um-state">Loading users...</div>
        ) : error ? (
          <div className="um-state um-state-error">{error}</div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>EMAIL</th>
                <th>DISPLAY NAME</th>
                <th>ROLE</th>
                <th colSpan={2}>SITES</th>
                <th>STATUS</th>
                <th className="um-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {addingUser && (
                <AddUserRow
                  allSites={allSites}
                  callerEmail={callerEmail}
                  onSaved={() => { setAddingUser(false); loadUsers(); }}
                  onCancel={() => setAddingUser(false)}
                />
              )}
              {users.length === 0 && !addingUser ? (
                <tr><td colSpan={7} className="um-state">No users match your filters.</td></tr>
              ) : (
                users.map(user => (
                  <UserRow
                    key={user.email}
                    user={user}
                    allSites={allSites}
                    callerEmail={callerEmail}
                    onSaved={loadUsers}
                    submittedOid={submittedOids[user.email] || null}
                    onOidSubmitted={noteSubmittedOid}
                  />
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
